"use client";

import { useRef, useState, type ReactNode } from "react";
import { MailCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { parseRateLimitError } from "@/utils/auth/rateLimitErrorParser";
import {
  hasVerifiedAccount,
  type AccountStatusUser,
} from "@/lib/auth/accountStatus";

interface AccountConfirmationStepProps {
  email: string;
  title?: string;
  description: ReactNode;
  detail?: ReactNode;
  buttonLabel?: string;
  footnote: ReactNode;
  onConfirmed?: () => void | Promise<void>;
  recoveryLabel?: string;
  onRecovery?: () => void;
}

interface SessionResponse {
  user?: AccountStatusUser | null;
  session?: { user?: AccountStatusUser | null } | null;
}

async function checkAccountConfirmation(): Promise<boolean> {
  const response = await fetch("/api/auth/session", {
    method: "GET",
    credentials: "include",
    cache: "no-store",
    signal: AbortSignal.timeout(15000),
  });
  if (!response.ok) throw new Error("Unable to check account confirmation");

  const payload = (await response.json()) as SessionResponse;
  return hasVerifiedAccount(payload.user ?? payload.session?.user);
}

/** Shared compact state shown while an anonymous account awaits verification. */
export function AccountConfirmationStep({
  email,
  title,
  description,
  detail,
  buttonLabel = "Check confirmation",
  footnote,
  onConfirmed,
  recoveryLabel,
  onRecovery,
}: AccountConfirmationStepProps) {
  const [checking, setChecking] = useState(false);
  const [resending, setResending] = useState(false);
  const busyRef = useRef(false);
  const [status, setStatus] = useState<string | null>(null);

  const handleConfirmationCheck = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setChecking(true);
    setStatus(null);

    try {
      if (onConfirmed) {
        await onConfirmed();
        return;
      }

      if (await checkAccountConfirmation()) {
        setStatus("Confirmed. Continuing…");
        window.location.reload();
        return;
      }

      setStatus("Not confirmed yet. Open the email link, then check again.");
    } catch {
      setStatus("We couldn’t check just now. Please try again.");
    } finally {
      busyRef.current = false;
      setChecking(false);
    }
  };

  const handleResend = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setResending(true);
    setStatus(null);
    try {
      const response = await fetch("/api/auth/resend-verification", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        signal: AbortSignal.timeout(15000),
        body: JSON.stringify({
          email,
          redirectTo:
            window.location.pathname +
            window.location.search +
            window.location.hash,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setStatus(
          response.status === 429
            ? parseRateLimitError(
                data.error_description ||
                  "Please wait before requesting another email.",
                true
              )
            : "We couldn't resend the email just now. Please try again."
        );
        return;
      }
      setStatus(
        "If your account is awaiting confirmation, a new verification link has been sent. Check your inbox and spam folder."
      );
    } catch {
      setStatus("We couldn't resend the email just now. Please try again.");
    } finally {
      busyRef.current = false;
      setResending(false);
    }
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="mx-auto flex max-w-sm flex-col items-center text-center">
        <span className="flex size-12 items-center justify-center rounded-full bg-primary-muted text-primary sm:size-14">
          <MailCheck className="size-6 sm:size-7" aria-hidden />
        </span>
        {title ? (
          <h3 className="mt-3 text-lg font-semibold text-foreground">
            {title}
          </h3>
        ) : null}
        <p
          className={`${title ? "mt-1" : "mt-3"} text-sm leading-5 text-muted-foreground`}
        >
          {description}{" "}
          <strong className="break-all font-semibold text-foreground">
            {email}
          </strong>
          .
        </p>
        {detail ? <div className="mt-4 w-full">{detail}</div> : null}
        <Button
          type="button"
          variant="gradient"
          onClick={() => void handleConfirmationCheck()}
          disabled={resending}
          loading={checking}
          loadingText="Checking…"
          className="mt-4 h-11 w-full rounded-xl text-sm font-semibold sm:h-12 sm:text-base"
        >
          {buttonLabel}
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => void handleResend()}
          disabled={checking || resending}
          className="mt-1 min-h-10 w-full px-2 text-xs font-semibold text-primary shadow-none"
        >
          {resending ? "Sending…" : "Resend email"}
        </Button>
        {status ? (
          <p
            role="status"
            aria-live="polite"
            className="mt-2 text-xs font-medium leading-4 text-foreground"
          >
            {status}
          </p>
        ) : null}
        <p className="mt-2 text-xs leading-4 text-muted-foreground">
          {footnote}
        </p>
        {recoveryLabel && onRecovery ? (
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setStatus(null);
              onRecovery();
            }}
            disabled={checking || resending}
            className="mt-1 min-h-10 w-full px-2 text-xs font-semibold text-primary shadow-none"
          >
            {recoveryLabel}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
